"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CustomListItem = void 0;
const core_1 = require("@tiptap/core");
const extension_list_item_1 = require("@tiptap/extension-list-item");
const extension_bold_1 = require("@tiptap/extension-bold");
const extension_italic_1 = require("@tiptap/extension-italic");
const state_1 = require("@tiptap/pm/state");
const lodash_1 = require("lodash");
const text_style_1 = require("./text-style");
const names_1 = require("./utils/names");
exports.CustomListItem = extension_list_item_1.ListItem.extend({
    name: names_1.CustomListItemExtensionName,
    renderHTML({ HTMLAttributes }) {
        return ['li', (0, core_1.mergeAttributes)(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
    addAttributes() {
        return {
            bold: {
                renderHTML: (attributes) => {
                    return {
                        style: `--tiptap-listitem-marker-fontweight: ${attributes.bold ? '700' : 'initial'}`,
                    };
                },
            },
            italic: {
                renderHTML: (attributes) => {
                    return {
                        style: `--tiptap-listitem-marker-fontstyle: ${attributes.italic ? 'italic' : 'initial'}`,
                    };
                },
            },
            fontFamily: {
                renderHTML: (attributes) => {
                    // We had to add " " around the font family name to support font families with numbers and special characters.
                    // Without the quotes, we were unable to apply font families like Source Sans 3
                    return {
                        style: `--tiptap-listitem-marker-fontfamily: ${attributes.fontFamily ? `"${attributes.fontFamily}"` : 'initial'}`,
                    };
                },
            },
            fontSize: {
                renderHTML: (attributes) => {
                    return {
                        style: `--tiptap-listitem-marker-fontsize: ${attributes.fontSize ? attributes.fontSize : 'initial'}`,
                    };
                },
            },
            color: {
                renderHTML: (attributes) => {
                    return {
                        style: `--tiptap-listitem-marker-color: ${attributes.color ? attributes.color : 'initial'}`,
                    };
                },
            },
            shadow: {
                renderHTML: (attributes) => {
                    if (!attributes.shadow) {
                        return {};
                    }
                    return {};
                },
            },
        };
    },
    addProseMirrorPlugins() {
        return [
            new state_1.Plugin({
                key: new state_1.PluginKey(`handle${names_1.CustomListItemExtensionName}`),
                appendTransaction: (transactions, oldState, newState) => {
                    let hasChanges = false;
                    let tr = newState.tr;
                    // If there's no selection range, there's no need to proceed
                    if (!tr.selection.ranges.length)
                        return null;
                    const selectionRange = tr.selection.ranges[0];
                    const selectionFromPosition = selectionRange.$from;
                    const listItemNodes = [];
                    newState.doc.descendants((node, pos) => {
                        if (node.type.name === names_1.CustomListItemExtensionName) {
                            listItemNodes.push({ node, pos });
                        }
                    });
                    // If the new state has no listItemNodes there's no need to proceed
                    if (!listItemNodes.length)
                        return null;
                    const targetedListItem = (0, lodash_1.last)(listItemNodes.filter(({ pos }) => pos < selectionFromPosition.pos));
                    // If the selection targets a list item we need to validate if it's one of the possible edge cases
                    if (targetedListItem) {
                        // In case of storedMarks, check if it's an empty listItemNode (to catch newline)
                        if (tr.storedMarks && targetedListItem.node.content.firstChild?.content.size === 0) {
                            const storedMarks = tr.storedMarks;
                            tr = tr.setNodeMarkup(targetedListItem.pos, undefined, {
                                ...getAttributesFromMarks(storedMarks),
                            });
                            /**
                             * ProseMirror clears the storedMarks after a setNodeMarkup/setNodeAttribute transaction, so we need to set them again
                             * @see https://discuss.prosemirror.net/t/does-tr-setnodeattribute-reset-stored-marks/6147
                             */
                            tr = tr.setStoredMarks(storedMarks);
                            return tr;
                        }
                        // When you navigate to an empty list item, which causes a reset of textStyle, we need to reflect it on the marker
                        if (selectionFromPosition.pos === selectionRange.$to.pos &&
                            targetedListItem.node.content.firstChild?.content.size === 0) {
                            tr = tr.setNodeMarkup(targetedListItem.pos, undefined, {});
                            hasChanges = true;
                        }
                    }
                    listItemNodes.map(({ node, pos }) => {
                        const attributes = processNodeAttributeChanges(node);
                        if (attributes) {
                            const storedMarks = tr.storedMarks;
                            tr = tr.setNodeMarkup(pos, undefined, {
                                ...attributes,
                            });
                            tr.setStoredMarks(storedMarks);
                            hasChanges = true;
                        }
                    });
                    return hasChanges ? tr : null;
                },
            }),
        ];
    },
    addOptions() {
        return {
            HTMLAttributes: {},
            bulletListTypeName: names_1.CustomBulletListExtensionName,
            orderedListTypeName: names_1.CustomOrderedListExtensionName,
            types: ['textStyle'],
        };
    },
});
function processNodeAttributeChanges(node) {
    const firstContentChildMarks = node.firstChild?.content.firstChild?.marks;
    if (!firstContentChildMarks)
        return null;
    const newAttributes = getAttributesFromMarks(firstContentChildMarks);
    return (0, lodash_1.isEqual)(node.attrs, newAttributes) ? null : newAttributes;
}
function getAttributesFromMarks(marks) {
    const markAttributes = marks.find((mark) => mark.type.name === text_style_1.TextStyle.name)?.attrs ?? {};
    const italicMark = marks.find((mark) => mark.type.name === extension_italic_1.Italic.name);
    const boldMark = marks.find((mark) => mark.type.name === extension_bold_1.Bold.name);
    const previousAttributes = {
        bold: !!boldMark,
        italic: !!italicMark,
        fontSize: markAttributes['fontSize'] ?? null,
        fontFamily: markAttributes['fontFamily'] ?? null,
        color: markAttributes['color'] ?? null,
        shadow: markAttributes['shadow'] ?? null,
    };
    return previousAttributes;
}
//# sourceMappingURL=custom-list-item.js.map